"use client";

import { useCallback, useMemo, useState } from "react";
import { PillGroup } from "@/components/tool-kit/primitives";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  GUIDELINE_FIELDS as F,
  formatGuideline,
  type GuidelineKind,
  type GuidelineLabels,
  guidelineIsReady,
} from "@/lib/tool-kit/citation-sources-more/clinical-practice-guideline-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const CLINICAL_PRACTICE_GUIDELINE_CITATION_GENERATOR_STRINGS = {
  formHeading: "Guideline details",
  resultsHeading: "Your citations",
  emptyPrompt:
    "Enter the guideline's title and its author to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  kindLabel: "Where did you read it?",
  kindOnline: "On the issuing body's website (NICE, WHO, USPSTF)",
  kindJournal: "Printed in a journal (AHA/ACC, KDIGO, JAMA)",
  organizationLabel: "Organization that wrote it",
  organizationPlaceholder: "National Institute for Health and Care Excellence",
  organizationHint:
    "Spelled out in full. If you fill this in, it is the author and the person names below are ignored.",
  authorsLabel: "Individual authors (if there is no organization)",
  authorsPlaceholder: "Paul K. Whelton",
  authorsHint:
    "One per line, as First Last or Last, First. Use Last, First for a compound surname, as in Dennison Himmelfarb, Cheryl.",
  titleLabel: "Guideline title",
  titlePlaceholder: "Hypertension in adults: diagnosis and management",
  titleHint:
    "Sentence case, with a lowercase letter after a colon as AMA and Vancouver want it. APA capitalizes the first word of the subtitle for you.",
  abbreviationLabel: "Organization's abbreviation (APA in-text, optional)",
  abbreviationPlaceholder: "NICE",
  abbreviationHint:
    "APA abbreviates a group author in the text, after spelling it out the first time.",
  publishedLabel: "Date published",
  updatedLabel: "Date last updated (optional)",
  accessedLabel: "Date you viewed it",
  datePlaceholder: "YYYY-MM-DD",
  publishedHint: "Journals: the year and month of the issue, as in 2018-06.",
  urlLabel: "URL of the guideline",
  urlPlaceholder: "https://www.nice.org.uk/guidance/ng136",
  seriesLabel: "Guideline number or series (optional)",
  seriesPlaceholder: "NICE guideline NG136",
  seriesHint:
    "As the document prints it. APA puts it in parentheses after the title.",
  publisherLabel: "Publisher or website name (optional)",
  publisherPlaceholder: "NICE",
  publisherHint:
    "Vancouver names the publisher. APA and AMA leave it out when it is the author's own name.",
  placeLabel: "Place of publication (Vancouver)",
  placePlaceholder: "London",
  journalLabel: "Journal (full title, for APA)",
  journalPlaceholder: "Hypertension",
  journalAbbrLabel: "Journal abbreviation (AMA and Vancouver)",
  journalAbbrPlaceholder: "Hypertension",
  journalAbbrHint: "The NLM abbreviation, from the NLM Catalog.",
  volumeLabel: "Volume",
  issueLabel: "Issue",
  pagesLabel: "Pages or article number",
  pagesPlaceholder: "e13-e115",
  doiLabel: "DOI",
  doiPlaceholder: "10.1161/HYP.0000000000000065",
  apaLabel: "APA 7th edition",
  amaLabel: "AMA 11th edition",
  vancouverLabel: "Vancouver (NLM)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  laterParenthetical: "Later in-text citations (parenthetical)",
  laterNarrative: "Later in-text citations (narrative)",
  apaNote:
    "APA 7 has no guideline type of its own. A web guideline is a report by a group author: the organization spelled out, the title in italics, the guideline number in parentheses, then the URL. A guideline in a journal is a journal article.",
  amaNote:
    "AMA cites a guideline printed in a journal as a journal article. A web guideline follows AMA's website pattern, with the guideline number as a sentence after the title — our choice, not an AMA example. In the text, cite it by its superscript reference number.",
  vancouverNote:
    "A web guideline follows NLM's Citing Medicine pattern for an Internet document with an organization as author. In the text, cite it by its reference number.",
  exampleOnline: {
    organization: "National Institute for Health and Care Excellence",
    title: "Hypertension in adults: diagnosis and management",
    abbreviation: "NICE",
    published: "2019-08-28",
    updated: "2026-02-26",
    accessed: "2026-10-16",
    url: "https://www.nice.org.uk/guidance/ng136",
    series: "NICE guideline NG136",
    publisher: "NICE",
    place: "London",
  },
  exampleJournal: {
    authors:
      "Paul K. Whelton\nRobert M. Carey\nWilbert S. Aronow\nDonald E. Casey Jr\nKaren J. Collins\nDennison Himmelfarb, Cheryl\nSondra M. DePalma\nSamuel Gidding\nKenneth A. Jamerson\nDaniel W. Jones\nEric J. MacLaughlin\nPaul Muntner\nBruce Ovbiagele\nSidney C. Smith Jr\nCrystal C. Spencer\nRandall S. Stafford\nSandra J. Taler\nRandal J. Thomas\nKim A. Williams Sr\nJeff D. Williamson\nJackson T. Wright Jr",
    title:
      "2017 ACC/AHA/AAPA/ABC/ACPM/AGS/APhA/ASH/ASPC/NMA/PCNA guideline for the prevention, detection, evaluation, and management of high blood pressure in adults: a report of the American College of Cardiology/American Heart Association Task Force on Clinical Practice Guidelines",
    published: "2018-06",
    journal: "Hypertension",
    journalAbbr: "Hypertension",
    volume: "71",
    issue: "6",
    pages: "e13-e115",
    doi: "10.1161/HYP.0000000000000065",
  },
} as const;

type Strings = typeof CLINICAL_PRACTICE_GUIDELINE_CITATION_GENERATOR_STRINGS;

function leadFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.organization,
      kind: "text",
      label: strings.organizationLabel,
      placeholder: strings.organizationPlaceholder,
      hint: strings.organizationHint,
    },
    {
      id: F.authors,
      kind: "people",
      label: strings.authorsLabel,
      placeholder: strings.authorsPlaceholder,
      hint: strings.authorsHint,
    },
    {
      id: F.title,
      kind: "text",
      label: strings.titleLabel,
      placeholder: strings.titlePlaceholder,
      hint: strings.titleHint,
      wide: true,
    },
  ];
}

function onlineFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.abbreviation,
      kind: "text",
      label: strings.abbreviationLabel,
      placeholder: strings.abbreviationPlaceholder,
      hint: strings.abbreviationHint,
    },
    {
      id: F.series,
      kind: "text",
      label: strings.seriesLabel,
      placeholder: strings.seriesPlaceholder,
      hint: strings.seriesHint,
    },
    {
      id: F.published,
      kind: "date",
      label: strings.publishedLabel,
      placeholder: strings.datePlaceholder,
    },
    {
      id: F.updated,
      kind: "date",
      label: strings.updatedLabel,
      placeholder: strings.datePlaceholder,
    },
    {
      id: F.accessed,
      kind: "date",
      label: strings.accessedLabel,
      placeholder: strings.datePlaceholder,
    },
    {
      id: F.publisher,
      kind: "text",
      label: strings.publisherLabel,
      placeholder: strings.publisherPlaceholder,
      hint: strings.publisherHint,
    },
    {
      id: F.place,
      kind: "text",
      label: strings.placeLabel,
      placeholder: strings.placePlaceholder,
    },
    {
      id: F.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholder,
    },
  ];
}

function journalFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.abbreviation,
      kind: "text",
      label: strings.abbreviationLabel,
      placeholder: strings.abbreviationPlaceholder,
      hint: strings.abbreviationHint,
    },
    {
      id: F.journal,
      kind: "text",
      label: strings.journalLabel,
      placeholder: strings.journalPlaceholder,
    },
    {
      id: F.journalAbbr,
      kind: "text",
      label: strings.journalAbbrLabel,
      placeholder: strings.journalAbbrPlaceholder,
      hint: strings.journalAbbrHint,
    },
    {
      id: F.published,
      kind: "date",
      label: strings.publishedLabel,
      placeholder: strings.datePlaceholder,
      hint: strings.publishedHint,
    },
    {
      id: F.volume,
      kind: "text",
      label: strings.volumeLabel,
    },
    {
      id: F.issue,
      kind: "text",
      label: strings.issueLabel,
    },
    {
      id: F.pages,
      kind: "text",
      label: strings.pagesLabel,
      placeholder: strings.pagesPlaceholder,
    },
    {
      id: F.doi,
      kind: "url",
      label: strings.doiLabel,
      placeholder: strings.doiPlaceholder,
    },
  ];
}

/** The widget's strings, as the formatter's labels. */
export function guidelineLabels(strings: Strings): GuidelineLabels {
  return {
    apa: strings.apaLabel,
    ama: strings.amaLabel,
    vancouver: strings.vancouverLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    laterParenthetical: strings.laterParenthetical,
    laterNarrative: strings.laterNarrative,
    apaNote: strings.apaNote,
    amaNote: strings.amaNote,
    vancouverNote: strings.vancouverNote,
  };
}

export default function ClinicalPracticeGuidelineCitationGeneratorWidget() {
  const strings = useToolUi(
    CLINICAL_PRACTICE_GUIDELINE_CITATION_GENERATOR_STRINGS
  );
  const [kind, setKind] = useState<GuidelineKind>("online");
  const fields = useMemo(
    () => [
      ...leadFields(strings),
      ...(kind === "online" ? onlineFields(strings) : journalFields(strings)),
    ],
    [strings, kind]
  );
  const labels = useMemo(() => guidelineLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatGuideline(kind, values, labels),
    [kind, labels]
  );

  return (
    <SourceCitationBuilder
      example={
        kind === "online" ? strings.exampleOnline : strings.exampleJournal
      }
      fields={fields}
      format={format}
      intro={
        <PillGroup
          label={strings.kindLabel}
          name="guideline-kind"
          onChange={setKind}
          options={[
            { value: "online", label: strings.kindOnline },
            { value: "journal", label: strings.kindJournal },
          ]}
          value={kind}
        />
      }
      isReady={guidelineIsReady}
      strings={strings}
    />
  );
}
